import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Clock, Film, Truck, LayoutGrid, Columns3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Seo } from '../components/Seo';
import { PackageCompare } from '../components/PackageCompare';
import { usePageContent } from '../hooks/usePageContent';
import { CATEGORIES, packagesFor, priceLabel, hours, SHARED_RULES, categorySlug } from '../lib/packageData';

const HERO_BG = 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1920';

const PackageCard = ({ pkg }) => (
  <Card className="bg-[#2A2420] border-[#C5A880]/20 h-full" data-testid={`pkg-${pkg.id}`}>
    <CardContent className="p-6 flex flex-col h-full">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-[#EADBC8] font-semibold text-lg">{pkg.name}</h3>
        <span className="text-[#C5A880] font-bold whitespace-nowrap">{priceLabel(pkg)}</span>
      </div>
      <div className="space-y-2 text-sm text-[#EADBC8]/70 flex-1">
        {pkg.minutes && <p className="flex items-center gap-2"><Clock size={15} className="text-[#C5A880]" /> Up to {hours(pkg.minutes)} on-site time</p>}
        {pkg.films && <p className="flex items-start gap-2"><Film size={15} className="text-[#C5A880] mt-0.5" /> {pkg.films}</p>}
        {pkg.scope && <p className="text-[#EADBC8]/55 text-xs pt-1">{pkg.scope}</p>}
      </div>
      <Link to="/quote" className="mt-5"><Button className="w-full bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold">Enquire / request a quote</Button></Link>
    </CardContent>
  </Card>
);

const Videography = () => {
  const { t } = usePageContent('videography');
  const [catId, setCatId] = useState(1);
  const [view, setView] = useState('cards');
  const list = packagesFor('Videography', catId);

  return (
    <div className="min-h-screen bg-[#1F1A17]">
      <Seo
        exactTitle="Videography — Wedding, Event & Personal Films | Mayden Films"
        description="Mayden Films videography: wedding films from $1,799 (enquiry-led), event highlights from $899, business video from $699, personal films from $599. On-site time includes setup and pack-down. AUD, no GST."
        path="/videography"
      />

      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={t('hero_image', HERO_BG)} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1F1A17]/85 via-[#1F1A17]/90 to-[#1F1A17]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C5A880] tracking-[0.25em] uppercase text-xs font-semibold mb-4">{t('hero_eyebrow', 'Videography')}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EADBC8] mb-5">{t('hero_title', 'Films & Highlights')}</h1>
          <p className="text-base md:text-lg text-[#EADBC8]/70 max-w-2xl mx-auto mb-6">
            {t('hero_subtitle', 'Planned coverage and defined finished films with private online delivery. Wedding films from $1,799, event highlights from $899, business video from $699, personal films from $599.')}
          </p>
          <Link to="/quote"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-8 py-6 text-base">Request a videography quote</Button></Link>
        </div>
      </section>

      <section className="py-16 bg-[#1A1612]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-2 text-center">Packages by category</h2>
          <p className="text-[#EADBC8]/55 text-sm text-center mb-2">All time allowances include setup, sound checks, breaks, location moves and pack-down.</p>
          <p className="text-[#EADBC8]/45 text-xs text-center mb-8">Wedding video is enquiry-led until production readiness and the timeline are confirmed. One videographer is not two roaming operators.</p>
          <div className="flex flex-nowrap sm:flex-wrap gap-2 mb-6 overflow-x-auto sm:justify-center pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {CATEGORIES.map((c) => (
              <button key={c.id} onClick={() => setCatId(c.id)} data-testid={`video-cat-${c.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap flex-shrink-0 transition-colors ${catId === c.id ? 'bg-[#C5A880] text-[#1F1A17]' : 'bg-[#2A2420] text-[#EADBC8]/70 hover:text-[#C5A880] border border-[#C5A880]/20'}`}>
                {c.name}
              </button>
            ))}
          </div>
          {list.length > 1 && (
            <div className="flex justify-center mb-8">
              <div className="inline-flex rounded-full border border-[#C5A880]/25 bg-[#2A2420] p-1" role="group" aria-label="View mode">
                <button onClick={() => setView('cards')} data-testid="video-view-cards"
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === 'cards' ? 'bg-[#C5A880] text-[#1F1A17]' : 'text-[#EADBC8]/65 hover:text-[#C5A880]'}`}>
                  <LayoutGrid size={15} /> Cards
                </button>
                <button onClick={() => setView('compare')} data-testid="video-view-compare"
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === 'compare' ? 'bg-[#C5A880] text-[#1F1A17]' : 'text-[#EADBC8]/65 hover:text-[#C5A880]'}`}>
                  <Columns3 size={15} /> Compare
                </button>
              </div>
            </div>
          )}
          {view === 'compare' && list.length > 1 ? (
            <PackageCompare service="Videography" packages={list} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="video-package-grid">
              {list.map((p) => <PackageCard key={p.id} pkg={p} />)}
            </div>
          )}
          {list.length === 0 && <p className="text-[#EADBC8]/50 text-center">This category is available as a custom quote — tell us about your project.</p>}
          <div className="text-center mt-8">
            <Link to={`/portfolio?category=${categorySlug(catId)}`} className="inline-flex items-center gap-1.5 text-[#C5A880] hover:text-[#D4BC95] font-medium text-sm transition-colors" data-testid="video-related-work">
              See related work in {CATEGORIES.find((c) => c.id === catId)?.name} →
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#1F1A17]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-6 text-center">Pricing &amp; booking</h2>
          <ul className="space-y-3">
            {SHARED_RULES.map((r) => (
              <li key={r} className="flex items-start text-[#EADBC8]/75 text-sm"><Check size={18} className="text-[#C5A880] mr-3 mt-0.5 flex-shrink-0" />{r}</li>
            ))}
          </ul>
          <div className="text-center mt-10">
            <Link to="/portfolio"><Button variant="outline" className="border-2 border-[#C5A880] text-[#C5A880] hover:bg-[#C5A880] hover:text-[#1F1A17] font-semibold px-8 py-5">View Our Work</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Videography;
